export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  coverImage: string;
  author: {
    name: string;
    avatar: string;
    role: string;
  };
  category: string;
  tags: string[];
  publishedAt: string;
  readTime: number;
  featured: boolean;
}

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  count: number;
}

// Sample blog posts data
export const blogPosts: BlogPost[] = [
  {
    id: "1",
    slug: "future-of-web-development-2026",
    title: "The Future of Web Development in 2026",
    excerpt:
      "Explore the cutting-edge technologies shaping the web development landscape, from AI-assisted coding to revolutionary frameworks that are changing how we build digital experiences.",
    content: `
# The Future of Web Development in 2026

The web development landscape is evolving at an unprecedented pace. As we navigate through 2026, several transformative technologies are reshaping how developers build, deploy, and maintain web applications.

## AI-Assisted Development

Artificial Intelligence has become an integral part of the development workflow. From intelligent code completion to automated testing, AI tools are augmenting developer capabilities in ways we couldn't have imagined just a few years ago.

### Key Innovations

- **Intelligent Code Generation**: AI models can now generate production-ready code from natural language descriptions
- **Automated Bug Detection**: Real-time analysis catches potential issues before they reach production
- **Smart Refactoring**: AI-powered tools suggest and implement code improvements automatically

## The Rise of Edge Computing

Edge computing has fundamentally changed how we think about web application architecture. By processing data closer to users, we've achieved:

- Sub-10ms response times globally
- Reduced server costs by 40-60%
- Enhanced privacy and data sovereignty

## Component-Based Architecture Evolution

The component-based paradigm continues to mature, with new patterns emerging:

1. **Server Components**: Blend of server-side rendering with client-side interactivity
2. **Island Architecture**: Hydrating only interactive components
3. **Resumability**: Instant page loads without hydration overhead

## What's Next?

As we look ahead, the convergence of AI, edge computing, and new architectural patterns promises even more exciting developments. The key is to stay adaptable and continue learning.

> "The best way to predict the future is to create it." - Alan Kay

Stay tuned for more insights on emerging web technologies and best practices.
    `,
    coverImage:
      "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&h=400&fit=crop",
    author: {
      name: "Alex Chen",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop",
      role: "Lead Developer",
    },
    category: "Technology",
    tags: ["Web Development", "AI", "Future Tech"],
    publishedAt: "2026-01-25",
    readTime: 8,
    featured: true,
  },
  {
    id: "2",
    slug: "building-scalable-apis-best-practices",
    title: "Building Scalable APIs: Best Practices for 2026",
    excerpt:
      "Learn the essential strategies for designing and implementing APIs that can handle millions of requests while maintaining performance and reliability.",
    content: `
# Building Scalable APIs: Best Practices for 2026

Creating APIs that scale effectively requires careful planning and implementation. This guide covers the essential strategies for building robust, high-performance APIs.

## Design Principles

### 1. RESTful Excellence

While GraphQL and gRPC have their place, REST remains the backbone of web APIs. Key principles include:

- **Consistent Naming**: Use plural nouns for resources
- **Proper HTTP Methods**: GET, POST, PUT, DELETE for CRUD operations
- **Meaningful Status Codes**: Communicate results clearly

### 2. Rate Limiting & Throttling

Protect your API from abuse while ensuring fair usage:

\`\`\`javascript
const rateLimit = {
  windowMs: 15 * 60 * 1000, // 15 minutes
  max: 100, // limit each IP to 100 requests per window
  standardHeaders: true,
};
\`\`\`

## Caching Strategies

Effective caching can reduce server load by up to 90%:

- **CDN Caching**: For static and semi-static content
- **Application Cache**: Redis or Memcached for dynamic data
- **Query Caching**: Avoid redundant database queries

## Monitoring & Observability

You can't improve what you can't measure:

| Metric | Target | Alert Threshold |
|--------|--------|-----------------|
| Response Time | <100ms | >500ms |
| Error Rate | <0.1% | >1% |
| Throughput | >1000 RPS | <500 RPS |

## Security Considerations

Never compromise on security:

- Implement OAuth 2.0 / JWT authentication
- Use HTTPS everywhere
- Validate and sanitize all inputs
- Implement proper CORS policies

Building scalable APIs is an ongoing journey. Stay updated with the latest best practices and continuously monitor your API's performance.
    `,
    coverImage:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&h=400&fit=crop",
    author: {
      name: "Sarah Johnson",
      avatar:
        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
      role: "Backend Architect",
    },
    category: "Development",
    tags: ["API", "Backend", "Scalability"],
    publishedAt: "2026-01-22",
    readTime: 12,
    featured: true,
  },
  {
    id: "3",
    slug: "mastering-typescript-advanced-patterns",
    title: "Mastering TypeScript: Advanced Patterns & Techniques",
    excerpt:
      "Dive deep into TypeScript's powerful type system with advanced patterns that will elevate your code quality and developer experience.",
    content: `
# Mastering TypeScript: Advanced Patterns & Techniques

TypeScript has become the de facto standard for large-scale JavaScript applications. Let's explore advanced patterns that can take your TypeScript skills to the next level.

## Advanced Type Patterns

### Conditional Types

\`\`\`typescript
type ExtractArrayType<T> = T extends (infer U)[] ? U : never;

type NumberArray = number[];
type ExtractedNumber = ExtractArrayType<NumberArray>; // number
\`\`\`

### Template Literal Types

\`\`\`typescript
type EventName<T extends string> = \`on\${Capitalize<T>}\`;
type ClickEvent = EventName<'click'>; // 'onClick'
\`\`\`

## Utility Type Deep Dive

TypeScript's built-in utility types are powerful, but creating custom ones is even better:

\`\`\`typescript
type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object 
    ? DeepPartial<T[P]> 
    : T[P];
};

type DeepReadonly<T> = {
  readonly [P in keyof T]: T[P] extends object 
    ? DeepReadonly<T[P]> 
    : T[P];
};
\`\`\`

## Best Practices

1. **Embrace Strict Mode**: Enable all strict checks
2. **Use Unknown Over Any**: Maintain type safety
3. **Leverage Inference**: Let TypeScript do the work
4. **Document with JSDoc**: Enhance IDE support

Mastering these patterns will help you write more maintainable, type-safe code that catches errors at compile time rather than runtime.
    `,
    coverImage:
      "https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=800&h=400&fit=crop",
    author: {
      name: "Michael Park",
      avatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop",
      role: "Senior Engineer",
    },
    category: "Development",
    tags: ["TypeScript", "JavaScript", "Best Practices"],
    publishedAt: "2026-01-20",
    readTime: 10,
    featured: true,
  },
  {
    id: "4",
    slug: "design-systems-at-scale",
    title: "Design Systems at Scale: Lessons from Enterprise Teams",
    excerpt:
      "Discover how leading enterprises build and maintain design systems that power consistent, accessible, and beautiful user experiences.",
    content: `
# Design Systems at Scale: Lessons from Enterprise Teams

Building a design system is one thing. Maintaining it across a large organization is another challenge entirely. Here's what we've learned from working with enterprise teams.

## Core Principles

### 1. Consistency Over Creativity

A design system's primary job is to create consistency. This means:

- **Standardized Components**: One button to rule them all
- **Unified Spacing**: Consistent rhythm across interfaces
- **Color Harmony**: A carefully curated palette

### 2. Accessibility First

Accessibility isn't an afterthought-it's foundational:

- WCAG 2.1 AA compliance minimum
- Keyboard navigation for all interactions
- Screen reader optimization

## Component Architecture

\`\`\`tsx
// Example: Composable Button Component
interface ButtonProps {
  variant: 'primary' | 'secondary' | 'ghost';
  size: 'sm' | 'md' | 'lg';
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  isLoading?: boolean;
}

export const Button = ({
  variant,
  size,
  children,
  ...props
}: ButtonProps) => {
  return (
    <button
      className={cn(
        baseStyles,
        variantStyles[variant],
        sizeStyles[size]
      )}
      {...props}
    >
      {children}
    </button>
  );
};
\`\`\`

## Governance & Adoption

Success requires more than good code:

1. **Documentation**: Comprehensive, searchable, with examples
2. **Training**: Workshops and ongoing education
3. **Feedback Loops**: Regular sync with consumers
4. **Versioning**: Semantic versioning with clear changelogs

A well-implemented design system can reduce development time by 30-50% while improving consistency and accessibility.
    `,
    coverImage:
      "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&h=400&fit=crop",
    author: {
      name: "Emily Zhang",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop",
      role: "Design Lead",
    },
    category: "Design",
    tags: ["Design Systems", "UI/UX", "Enterprise"],
    publishedAt: "2026-01-18",
    readTime: 7,
    featured: false,
  },
  {
    id: "5",
    slug: "cloud-native-architecture-patterns",
    title: "Cloud-Native Architecture Patterns for Modern Apps",
    excerpt:
      "Explore the essential architectural patterns that power cloud-native applications, from microservices to serverless and beyond.",
    content: `
# Cloud-Native Architecture Patterns for Modern Apps

Cloud-native development has transformed how we build and deploy applications. Let's explore the key patterns that enable scalable, resilient systems.

## Microservices Architecture

### Service Decomposition

Break monoliths into focused services:

- **Single Responsibility**: Each service does one thing well
- **Bounded Contexts**: Clear domain boundaries
- **Independent Deployment**: Update without affecting others

### Communication Patterns

- **Synchronous**: REST, gRPC for real-time needs
- **Asynchronous**: Message queues for decoupling
- **Event-Driven**: Publish-subscribe for scalability

## Container Orchestration

Kubernetes has become the standard for container orchestration:

\`\`\`yaml
apiVersion: apps/v1
kind: Deployment
metadata:
  name: web-service
spec:
  replicas: 3
  selector:
    matchLabels:
      app: web
  template:
    spec:
      containers:
      - name: web
        image: app:latest
        resources:
          limits:
            memory: "256Mi"
            cpu: "500m"
\`\`\`

## Serverless Patterns

When to go serverless:

| Use Case | Serverless Fit |
|----------|---------------|
| APIs | ✅ Excellent |
| Background Jobs | ✅ Excellent |
| Real-time Streaming | ⚠️ Consider |
| Long-running Processes | ❌ Not Ideal |

## Observability

The three pillars of observability:

1. **Logs**: Structured, centralized logging
2. **Metrics**: Real-time performance data
3. **Traces**: Distributed request tracing

Cloud-native architecture isn't just about technology-it's about enabling teams to move fast while maintaining reliability.
    `,
    coverImage:
      "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=400&fit=crop",
    author: {
      name: "David Kim",
      avatar:
        "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop",
      role: "Cloud Architect",
    },
    category: "Infrastructure",
    tags: ["Cloud", "Architecture", "DevOps"],
    publishedAt: "2026-01-15",
    readTime: 11,
    featured: false,
  },
  {
    id: "6",
    slug: "ai-powered-development-tools",
    title: "AI-Powered Development Tools That Actually Work",
    excerpt:
      "A practical guide to AI development tools that genuinely boost productivity, with real-world examples and integration strategies.",
    content: `
# AI-Powered Development Tools That Actually Work

The AI tooling landscape is crowded, but not all tools deliver on their promises. Here's our curated list of AI-powered development tools that genuinely boost productivity.

## Code Assistance

### Intelligent Autocompletion

Modern AI coding assistants go beyond simple autocomplete:

- Context-aware suggestions
- Multi-file understanding
- Natural language to code conversion

### Code Review Automation

AI-powered code review can catch:

- Security vulnerabilities
- Performance issues
- Style inconsistencies
- Potential bugs

## Documentation Generation

Generate comprehensive docs automatically:

\`\`\`javascript
/**
 * AI-generated documentation
 * Calculates the optimal cache TTL based on content type and access patterns
 * @param contentType - The MIME type of the content
 * @param accessFrequency - Average accesses per hour
 * @returns Optimal TTL in seconds
 */
function calculateCacheTTL(
  contentType: string, 
  accessFrequency: number
): number {
  // Implementation
}
\`\`\`

## Testing & QA

AI-assisted testing capabilities:

1. **Test Generation**: Automatic test case creation
2. **Mutation Testing**: Find gaps in test coverage
3. **Visual Regression**: Detect UI changes
4. **Performance Testing**: Identify bottlenecks

## Best Practices for AI Tool Adoption

- Start small with one or two tools
- Measure productivity impact
- Establish guidelines for AI-generated code
- Maintain human oversight

The key is finding tools that integrate seamlessly into your workflow without adding friction.
    `,
    coverImage:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&h=400&fit=crop",
    author: {
      name: "Lisa Wang",
      avatar:
        "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100&h=100&fit=crop",
      role: "DevOps Engineer",
    },
    category: "Technology",
    tags: ["AI", "Developer Tools", "Productivity"],
    publishedAt: "2026-01-12",
    readTime: 9,
    featured: false,
  },
  {
    id: "7",
    slug: "react-performance-optimization-guide",
    title: "React Performance Optimization: The Complete Guide",
    excerpt:
      "Master React performance with proven optimization techniques, from memoization to code splitting and everything in between.",
    content: `
# React Performance Optimization: The Complete Guide

Performance optimization in React requires understanding how React renders and updates the DOM. Let's explore proven techniques to build lightning-fast React applications.

## Understanding React Renders

### The Render Cycle

React re-renders when:
- State changes
- Props change
- Parent component renders
- Context value changes

### Profiling Tools

Use React DevTools Profiler to:
- Identify slow components
- Track render counts
- Analyze commit phases

## Memoization Strategies

### React.memo

Prevent unnecessary re-renders:

\`\`\`tsx
const ExpensiveComponent = React.memo(({ data }) => {
  // Expensive rendering logic
  return <div>{/* ... */}</div>;
});
\`\`\`

### useMemo and useCallback

\`\`\`tsx
const MemoizedValue = () => {
  const expensiveValue = useMemo(() => {
    return computeExpensiveValue(deps);
  }, [deps]);

  const stableCallback = useCallback(() => {
    handleAction(id);
  }, [id]);

  return <Child value={expensiveValue} onClick={stableCallback} />;
};
\`\`\`

## Code Splitting

Reduce initial bundle size:

\`\`\`tsx
const LazyComponent = React.lazy(() => import('./HeavyComponent'));

function App() {
  return (
    <Suspense fallback={<Loading />}>
      <LazyComponent />
    </Suspense>
  );
}
\`\`\`

## Virtual Lists

For long lists, virtualization is essential:

- Only render visible items
- Recycle DOM nodes
- Maintain scroll position

## Quick Wins Checklist

- [ ] Use production builds
- [ ] Implement code splitting
- [ ] Memoize expensive calculations
- [ ] Virtualize long lists
- [ ] Optimize images and assets

Performance optimization is an ongoing process. Measure, optimize, and repeat.
    `,
    coverImage:
      "https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=800&h=400&fit=crop",
    author: {
      name: "Alex Chen",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop",
      role: "Lead Developer",
    },
    category: "Development",
    tags: ["React", "Performance", "Optimization"],
    publishedAt: "2026-01-10",
    readTime: 14,
    featured: false,
  },
  {
    id: "8",
    slug: "security-best-practices-web-apps",
    title: "Security Best Practices for Modern Web Applications",
    excerpt:
      "Protect your web applications with comprehensive security strategies covering authentication, data protection, and vulnerability prevention.",
    content: `
# Security Best Practices for Modern Web Applications

Security isn't optional-it's essential. This guide covers the critical security practices every web developer should implement.

## Authentication & Authorization

### Modern Auth Strategies

- **OAuth 2.0 / OIDC**: Industry standard for authorization
- **Passkeys**: Passwordless authentication
- **MFA**: Multi-factor authentication as default

### Token Security

\`\`\`javascript
// Secure JWT handling
const tokenConfig = {
  algorithm: 'RS256',
  expiresIn: '15m',
  issuer: 'your-app',
  audience: 'your-api',
};

// Always validate tokens server-side
const validateToken = (token) => {
  try {
    return jwt.verify(token, publicKey, tokenConfig);
  } catch (error) {
    throw new AuthenticationError('Invalid token');
  }
};
\`\`\`

## Data Protection

### Encryption at Rest and Transit

- TLS 1.3 for all connections
- AES-256 for stored data
- Proper key management

### Input Validation

Never trust user input:

\`\`\`javascript
// Validate and sanitize
const sanitizedInput = {
  email: validator.normalizeEmail(input.email),
  name: validator.escape(input.name),
  url: validator.isURL(input.url) ? input.url : null,
};
\`\`\`

## Common Vulnerabilities

| Vulnerability | Prevention |
|--------------|------------|
| XSS | Content Security Policy, sanitization |
| CSRF | SameSite cookies, tokens |
| SQL Injection | Parameterized queries |
| SSRF | URL validation, allowlists |

## Security Headers

Essential headers to implement:

- Content-Security-Policy
- X-Frame-Options
- X-Content-Type-Options
- Strict-Transport-Security

## Incident Response

Prepare for security incidents:

1. Detection and alerting
2. Containment procedures
3. Investigation process
4. Recovery steps
5. Post-mortem analysis

Security is a continuous process. Stay updated on threats and regularly audit your applications.
    `,
    coverImage:
      "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&h=400&fit=crop",
    author: {
      name: "James Wilson",
      avatar:
        "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop",
      role: "Security Engineer",
    },
    category: "Security",
    tags: ["Security", "Web Development", "Best Practices"],
    publishedAt: "2026-01-08",
    readTime: 13,
    featured: false,
  },
];

// Get featured posts
export const getFeaturedPosts = (count: number = 3): BlogPost[] => {
  return blogPosts.filter((post) => post.featured).slice(0, count);
};

// Get all posts sorted by date
export const getAllPosts = (): BlogPost[] => {
  return [...blogPosts].sort(
    (a, b) =>
      new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
  );
};

/** Merge AI-generated ephemeral posts with static evergreen posts. Generated wins on slug clash. */
export const mergePosts = (
  staticPosts: BlogPost[],
  generatedPosts: BlogPost[],
): BlogPost[] => {
  const generatedSlugs = new Set(generatedPosts.map((p) => p.slug));
  const evergreen = staticPosts.filter((p) => !generatedSlugs.has(p.slug));
  return [...generatedPosts, ...evergreen].sort(
    (a, b) =>
      new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
  );
};

export const getCategoriesFromPosts = (posts: BlogPost[]): BlogCategory[] => {
  const categoryMap = new Map<string, number>();
  posts.forEach((post) => {
    const count = categoryMap.get(post.category) || 0;
    categoryMap.set(post.category, count + 1);
  });
  return Array.from(categoryMap.entries()).map(([name, count]) => ({
    id: name.toLowerCase(),
    name,
    slug: name.toLowerCase().replace(/\s+/g, "-"),
    description: `Articles about ${name}`,
    count,
  }));
};

// Get post by slug
export const getPostBySlug = (slug: string): BlogPost | undefined => {
  return blogPosts.find((post) => post.slug === slug);
};

// Get posts by category
export const getPostsByCategory = (category: string): BlogPost[] => {
  return blogPosts.filter(
    (post) => post.category.toLowerCase() === category.toLowerCase(),
  );
};

// Get all unique categories
export const getCategories = (): BlogCategory[] => {
  const categoryMap = new Map<string, number>();

  blogPosts.forEach((post) => {
    const count = categoryMap.get(post.category) || 0;
    categoryMap.set(post.category, count + 1);
  });

  return Array.from(categoryMap.entries()).map(([name, count]) => ({
    id: name.toLowerCase(),
    name,
    slug: name.toLowerCase().replace(/\s+/g, "-"),
    description: `Articles about ${name}`,
    count,
  }));
};

// Get related posts
export const getRelatedPosts = (
  currentSlug: string,
  count: number = 3,
  pool?: BlogPost[],
): BlogPost[] => {
  const all = pool ?? getAllPosts();
  const currentPost = all.find((post) => post.slug === currentSlug);
  if (!currentPost) return [];

  return all
    .filter((post) => post.slug !== currentSlug)
    .filter(
      (post) =>
        post.category === currentPost.category ||
        post.tags.some((tag) => currentPost.tags.includes(tag)),
    )
    .slice(0, count);
};

// Search posts
export const searchPosts = (query: string, pool?: BlogPost[]): BlogPost[] => {
  const lowercaseQuery = query.toLowerCase();
  const all = pool ?? getAllPosts();
  return all.filter(
    (post) =>
      post.title.toLowerCase().includes(lowercaseQuery) ||
      post.excerpt.toLowerCase().includes(lowercaseQuery) ||
      post.tags.some((tag) => tag.toLowerCase().includes(lowercaseQuery)),
  );
};

// Get blog subdomain URL
export const getBlogUrl = (path: string = ""): string => {
  const isDevelopment =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  if (isDevelopment) {
    // In development, we'll use the same origin with /blog prefix
    // since subdomain on localhost requires hosts file configuration
    return `/blog${path}`;
  }

  // In production, use the blog subdomain
  return `https://blog.devdotcom.org${path}`;
};
